/**
 * Publication orders — one screen, two audiences.
 *
 * EGBA sees a queue to work through; a school sees its own orders. Building two
 * screens would mean two places to fix the same bug, and the difference between
 * them is genuinely small: which columns matter and which buttons appear. The
 * buttons come from the server (`acoes`), derived from the same state machine
 * that will accept or refuse the click — so the interface cannot offer an
 * action the API would then reject.
 *
 * The spreadsheet is validated in the browser before upload, with the same
 * functions the server runs. A file carrying a CPF is refused here and never
 * leaves the school's computer.
 */

import {
  ROTULOS_STATUS,
  resumirProblemas,
  validarColunasConcluintes,
  validarLinhasConcluintes,
  type Demonstracao,
  type ProblemaLinha,
  type StatusPedido,
  type Transicao,
} from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api, type Item } from '../api/cliente'
import { EditorDemonstracoes } from '../componentes/intake/EditorDemonstracoes'
import { PreviaDiario } from '../componentes/intake/PreviaDiario'
import { podeAdministrar, useAuth } from '../estado/auth'
import { lerArquivoComoGrade } from '../util/planilha'
import {
  AreaTexto,
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Modal,
  Tabela,
  Vazio,
  formatarBRL,
  formatarData,
  formatarDataCurta,
} from '../componentes/ui'

const URL_API = import.meta.env.VITE_API_URL ?? ''

interface Pedido {
  id: string
  protocolo: string
  status: StatusPedido
  tipo: string
  versao: number
  organizacaoId: string
  categoriaId: string
  organizacao: string | null
  categoria: string | null
  valorCentavos: number | null
  previsaoPublicacao: string | null
  edicaoDoe: string | null
  dataPublicacao: string | null
  motivoRecusa: string | null
  concluintes: number
  criadoEm: string
  atualizadoEm: string
}

interface Detalhe extends Omit<Pedido, 'organizacao' | 'categoria'> {
  organizacao: { razaoSocial: string; cnpj: string; situacaoRegulatoria: string; logoUrl?: string | null } | null
  categoria: {
    id: string
    nome: string
    slaPublicacaoDias: number
    /** planilha_nominal | demonstracoes_contabeis */
    formaIntake: string
  } | null
  totalConcluintes: number
  alturaCm: number | null
  colunas: number | null
  pagina: string | null
  memoriaCalculo: Array<{ descricao: string; valorCentavos: number }> | null
  arquivoOriginalId: string | null
  materiaDiagramadaId: string | null
  comprovantePagamentoId: string | null
  acoes: Transicao[]
  instrucoesPagamento: Record<string, string> | null
  rotuloStatus: { nome: string; descricao: string } | null
}

const TOM_STATUS: Record<string, 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo'> = {
  rascunho: 'neutro',
  em_validacao: 'marca',
  aguardando_sec: 'marca',
  aguardando_aprovacao: 'atencao',
  aguardando_pagamento: 'atencao',
  pago: 'marca',
  enviado_egba: 'marca',
  publicado: 'sucesso',
  recusado: 'perigo',
  cancelado: 'neutro',
}

/** Statuses the EGBA queue groups by — the order someone works through them. */
const FILA: StatusPedido[] = [
  'em_validacao',
  'aguardando_sec',
  'aguardando_aprovacao',
  'aguardando_pagamento',
  'pago',
  'enviado_egba',
]

export function Pedidos() {
  const { usuario } = useAuth()
  const admin = podeAdministrar(usuario?.papel)
  // `?abrir=<id>` permite chegar direto na ficha — é como o criador em tela
  // cheia devolve quem acabou de enviar um pedido.
  const [params, setParams] = useSearchParams()
  const [abertoLocal, setAbertoLocal] = useState<string | null>(null)
  const abertoId = abertoLocal ?? params.get('abrir')
  const fechar = () => {
    setAbertoLocal(null)
    if (params.get('abrir')) setParams({}, { replace: true })
  }

  if (abertoId) return <DetalhePedido id={abertoId} aoVoltar={fechar} />
  return <Listagem admin={admin} aoAbrir={setAbertoLocal} />
}

// ---------------------------------------------------------------- listagem ---

function Listagem({ admin, aoAbrir }: { admin: boolean; aoAbrir: (id: string) => void }) {
  const [status, setStatus] = useState<string>('')
  const [pagina, setPagina] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['pedidos', status, pagina],
    queryFn: () => {
      // Paginado de verdade: a chamada sem `pagina` recebia os 30 primeiros e
      // a tela mostrava esses 30 como se fossem tudo, enquanto os cartões
      // contavam a fila inteira. A partir do 31º pedido, o que sumia da tela
      // era justamente o mais antigo — o que está esperando há mais tempo.
      const q = new URLSearchParams({ pagina: String(pagina), porPagina: '30' })
      if (status) q.set('status', status)
      return api.get<{ dados: Pedido[]; total: number; porStatus: Record<string, number> }>(
        `/api/v1/pedidos?${q.toString()}`,
      )
    },
  })

  if (isLoading) return <Carregando />

  const porStatus = data?.porStatus ?? {}
  const pedidos = data?.dados ?? []

  return (
    <div className="space-y-6">
      {!admin && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(['rascunho', 'em_validacao', 'aguardando_aprovacao', 'publicado'] as StatusPedido[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatus(status === s ? '' : s)
                setPagina(1)
              }}
              className={`cartao p-4 text-left transition ${
                status === s ? 'ring-2 ring-marca-500' : 'hover:border-marca-300'
              }`}
            >
              <p className="text-2xl font-semibold text-slate-900 tabular">{porStatus[s] ?? 0}</p>
              <p className="mt-0.5 text-xs leading-tight text-slate-500">{ROTULOS_STATUS[s].nome}</p>
            </button>
          ))}
        </div>
      )}

      {admin && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {FILA.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatus(status === s ? '' : s)
                setPagina(1)
              }}
              className={`cartao p-4 text-left transition ${
                status === s ? 'ring-2 ring-marca-500' : 'hover:border-marca-300'
              }`}
            >
              <p className="text-2xl font-semibold text-slate-900 tabular">{porStatus[s] ?? 0}</p>
              <p className="mt-0.5 text-xs leading-tight text-slate-500">
                {ROTULOS_STATUS[s].nome}
              </p>
            </button>
          ))}
        </div>
      )}

      <Cartao
        titulo={admin ? 'Fila de publicação' : 'Meus pedidos'}
        descricao={
          admin
            ? 'Pedidos das instituições, na ordem em que precisam de atenção.'
            : 'Acompanhe cada pedido de publicação da sua instituição.'
        }
        acoes={<Link to="/pedidos/novo"><Botao>Novo pedido</Botao></Link>}
      >
        {pedidos.length === 0 ? (
          <Vazio
            titulo={status ? 'Nada nesta situação' : 'Nenhum pedido ainda'}
            descricao={
              status
                ? 'Escolha outra situação ou limpe o filtro.'
                : 'Crie um pedido, envie o conteúdo da publicação e acompanhe até a saída no Diário Oficial.'
            }
            acao={<Link to="/pedidos/novo"><Botao>Criar o primeiro pedido</Botao></Link>}
          />
        ) : (
          <Tabela>
            <Cabecalho
              colunas={[
                'Protocolo',
                ...(admin ? ['Instituição'] : []),
                'Matéria',
                'Situação',
                'Valor',
                'Previsão',
                'Atualizado',
                '',
              ]}
            />
            <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
              {pedidos.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900 tabular">{p.protocolo}</p>
                    {p.tipo === 'retificacao' && (
                      <span className="text-xs text-amber-700">retificação</span>
                    )}
                  </td>
                  {admin && (
                    <td className="max-w-56 px-4 py-3">
                      <p className="truncate text-sm text-slate-700">{p.organizacao ?? '—'}</p>
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <p>{p.categoria ?? '—'}</p>
                    {p.concluintes > 0 && (
                      <p className="text-xs text-slate-400">{p.concluintes} registros</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Etiqueta tom={TOM_STATUS[p.status] ?? 'neutro'}>
                      {ROTULOS_STATUS[p.status]?.nome ?? p.status}
                    </Etiqueta>
                    {p.status === 'publicado' && p.dataPublicacao && (
                      <p className="mt-0.5 text-xs text-slate-400">
                        {p.edicaoDoe} · {formatarDataCurta(p.dataPublicacao)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700 tabular">
                    {p.valorCentavos != null ? formatarBRL(p.valorCentavos) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 tabular">
                    {p.status === 'publicado'
                      ? formatarDataCurta(p.dataPublicacao)
                      : p.previsaoPublicacao
                        ? formatarDataCurta(p.previsaoPublicacao)
                        : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatarData(p.atualizadoEm)}</td>
                  <td className="px-4 py-3 text-right">
                    <Botao
                      variante={['rascunho', 'aguardando_aprovacao'].includes(p.status) ? 'primario' : 'secundario'}
                      tamanho="sm"
                      onClick={() => aoAbrir(p.id)}
                    >
                      {p.status === 'rascunho'
                        ? 'Continuar'
                        : p.status === 'aguardando_aprovacao'
                          ? 'Revisar valor'
                          : 'Abrir'}
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}

        {(data?.total ?? 0) > 30 && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>
              {data!.total} pedidos · página {pagina} de {Math.ceil(data!.total / 30)}
            </span>
            <div className="flex gap-2">
              <Botao
                variante="secundario"
                tamanho="sm"
                disabled={pagina <= 1}
                onClick={() => setPagina((x) => x - 1)}
              >
                Anterior
              </Botao>
              <Botao
                variante="secundario"
                tamanho="sm"
                disabled={pagina >= Math.ceil(data!.total / 30)}
                onClick={() => setPagina((x) => x + 1)}
              >
                Próxima
              </Botao>
            </div>
          </div>
        )}
      </Cartao>

    </div>
  )
}

// ------------------------------------------------------------------ detalhe ---

function DetalhePedido({ id, aoVoltar }: { id: string; aoVoltar: () => void }) {
  const qc = useQueryClient()
  const { usuario } = useAuth()
  const admin = podeAdministrar(usuario?.papel)

  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [acaoAberta, setAcaoAberta] = useState<Transicao | null>(null)
  const [motivo, setMotivo] = useState('')
  const [alturaCm, setAlturaCm] = useState('')
  const [colunas, setColunas] = useState('3')
  const [edicaoDoe, setEdicaoDoe] = useState('')
  const [dataPublicacao, setDataPublicacao] = useState('')
  const [pagina, setPagina] = useState('')

  const [previa, setPrevia] = useState<{
    total: number
    problemas: ProblemaLinha[]
    resumo: string
    bloqueado: boolean
  } | null>(null)
  const [enviando, setEnviando] = useState(false)
  const entradaArquivo = useRef<HTMLInputElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['pedido', id],
    queryFn: () => api.get<Item<Detalhe>>(`/api/v1/pedidos/${id}`),
  })

  const p = data?.dados

  const transicionar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      api.post<Item<Detalhe>>(`/api/v1/pedidos/${id}/transicao`, {
        versaoEsperada: p!.versao,
        ...corpo,
      }),
    onSuccess: () => {
      setAcaoAberta(null)
      setMotivo('')
      setErro(null)
      void qc.invalidateQueries({ queryKey: ['pedido', id] })
      void qc.invalidateQueries({ queryKey: ['pedidos'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  /**
   * Validates the file locally, then uploads.
   *
   * The local pass is not the control — the server repeats it on the bytes it
   * receives — but it is what keeps a spreadsheet full of CPFs from ever
   * leaving the school's machine.
   */
  async function aoEscolherArquivo(arquivo: File): Promise<void> {
    setErro(null)
    setPrevia(null)

    let grade
    try {
      grade = await lerArquivoComoGrade(arquivo)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui ler este arquivo.')
      return
    }

    const colunasOk = validarColunasConcluintes(grade.cabecalhos)
    if (!colunasOk.valido) {
      setErro(colunasOk.mensagem)
      return
    }

    const r = validarLinhasConcluintes(grade.cabecalhos, grade.linhas)
    setPrevia({
      total: r.linhas.length,
      problemas: r.problemas.slice(0, 30),
      resumo: resumirProblemas(r),
      bloqueado: !r.valido,
    })

    if (!r.valido) return

    setEnviando(true)
    try {
      const json = await api.enviarArquivo<Item<{ total: number }>>(
        `/api/v1/pedidos/${id}/planilha`,
        arquivo,
      )
      setAviso(`${json.dados.total} concluintes recebidos.`)
      void qc.invalidateQueries({ queryKey: ['pedido', id] })
      // Sem isto o cartão da relação continuava mostrando a turma anterior:
      // a chave é outra, e o staleTime de 30s segura o cache.
      void qc.invalidateQueries({ queryKey: ['concluintes', id] })
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha no envio.')
    } finally {
      setEnviando(false)
    }
  }

  if (isLoading || !p) return <Carregando />

  function abrirAcao(t: Transicao): void {
    setAcaoAberta(t)
    setMotivo('')
    setErro(null)
    if (t.para === 'aguardando_aprovacao') {
      setAlturaCm(p!.alturaCm ? String(p!.alturaCm) : '')
      setColunas(p!.colunas ? String(p!.colunas) : '3')
    }
    if (t.para === 'publicado') {
      setEdicaoDoe(p!.edicaoDoe ?? '')
      setDataPublicacao(p!.dataPublicacao ?? new Date().toISOString().slice(0, 10))
      setPagina(p!.pagina ?? '')
    }
  }

  /**
   * Whether the dialog has what the transition needs.
   *
   * The button used to be disabled only when a reason was required, so
   * "Registrar publicação" could be confirmed with edition and page blank — and
   * the certificate, which is what the school hands to the Conselho, came out
   * empty.
   */
  function acaoPreenchida(): boolean {
    const t = acaoAberta
    if (!t) return false
    if (t.exigeMotivo && motivo.trim().length < 10) return false
    if (t.para === 'aguardando_aprovacao') {
      return Number(alturaCm) >= 1 && Number(colunas) >= 1
    }
    if (t.para === 'publicado') {
      return (
        edicaoDoe.trim().length > 0 &&
        /^\d{4}-\d{2}-\d{2}$/.test(dataPublicacao) &&
        pagina.trim().length > 0
      )
    }
    return true
  }

  function confirmarAcao(): void {
    const t = acaoAberta
    if (!t) return
    const corpo: Record<string, unknown> = { para: t.para }
    if (t.exigeMotivo) corpo.motivo = motivo
    if (t.para === 'aguardando_aprovacao') {
      corpo.alturaCm = Number(alturaCm)
      corpo.colunas = Number(colunas)
    }
    if (t.para === 'publicado') {
      corpo.edicaoDoe = edicaoDoe.trim()
      corpo.dataPublicacao = dataPublicacao
      corpo.pagina = pagina.trim()
    }
    if (t.para === 'em_validacao') corpo.avisosCientes = true
    transicionar.mutate(corpo)
  }

  const editavel = p.status === 'rascunho'

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------- cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={aoVoltar}
            className="mb-1 text-sm text-slate-500 hover:text-slate-900"
          >
            ← Pedidos
          </button>
          <h2 className="text-xl font-semibold text-slate-900 tabular">{p.protocolo}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Etiqueta tom={TOM_STATUS[p.status] ?? 'neutro'}>
              {p.rotuloStatus?.nome ?? p.status}
            </Etiqueta>
            <span className="text-sm text-slate-500">{p.rotuloStatus?.descricao}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {p.status === 'publicado' && (
            <a
              href={`${URL_API}/api/v1/pedidos/${p.id}/certidao`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-lg bg-marca-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-marca-700"
            >
              Certidão de publicação
            </a>
          )}
          {p.acoes.map((t) => (
            <Botao
              key={`${t.de}-${t.para}`}
              variante={t.para === 'recusado' || t.para === 'cancelado' ? 'perigo' : 'secundario'}
              onClick={() => abrirAcao(t)}
            >
              {t.rotulo}
            </Botao>
          ))}
        </div>
      </div>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {aviso && !erro && <Aviso tom="sucesso">{aviso}</Aviso>}

      {p.motivoRecusa && (
        <Aviso tom="atencao" titulo="A EGBA registrou um motivo">
          {p.motivoRecusa}
        </Aviso>
      )}

      {/* ------------------------------------------------------------ resumo */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Cartao titulo="Pedido" className="lg:col-span-2">
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {[
              ['Instituição', p.organizacao?.razaoSocial ?? '—'],
              ['Matéria', p.categoria?.nome ?? '—'],
              ['Registros na relação', String(p.totalConcluintes)],
              [
                'Previsão de publicação',
                p.previsaoPublicacao ? formatarDataCurta(p.previsaoPublicacao) : '—',
              ],
              ...(p.alturaCm
                ? [['Medida da matéria', `${p.alturaCm} cm × ${p.colunas} coluna(s)`]]
                : []),
              ...(p.status === 'publicado'
                ? [
                    ['Edição', p.edicaoDoe ?? '—'],
                    ['Data', p.dataPublicacao ? formatarDataCurta(p.dataPublicacao) : '—'],
                    ['Página', p.pagina ?? '—'],
                  ]
                : []),
            ].map(([rotulo, valor]) => (
              <div key={rotulo}>
                <dt className="text-xs tracking-wide text-slate-400 uppercase">{rotulo}</dt>
                <dd className="text-sm text-slate-800">{valor}</dd>
              </div>
            ))}
          </dl>
        </Cartao>

        <Cartao titulo="Valor">
          {p.valorCentavos != null ? (
            <>
              <p className="text-3xl font-semibold text-slate-900 tabular">
                {formatarBRL(p.valorCentavos)}
              </p>
              {p.memoriaCalculo && (
                <ul className="mt-4 space-y-1.5 border-t border-[color:var(--color-borda-suave)] pt-3 text-xs">
                  {p.memoriaCalculo.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3 text-slate-500">
                      <span>{l.descricao}</span>
                      <span className="tabular">{formatarBRL(l.valorCentavos)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">
              O valor é apurado depois da diagramação, sobre a medida real da matéria. A tabela
              vigente é por centímetro de coluna.
            </p>
          )}
        </Cartao>
      </div>

      {/* ------------------------------------------------------- pagamento */}
      {p.status === 'aguardando_pagamento' && p.instrucoesPagamento && (
        <Cartao titulo={p.instrucoesPagamento.titulo ?? 'Recolhimento'}>
          <p className="text-sm whitespace-pre-line text-slate-700">
            {p.instrucoesPagamento.instrucoes}
          </p>
          <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            {(
              [
                ['Favorecido', 'favorecido'],
                ['Chave PIX', 'chavePix'],
                ['Banco', 'banco'],
                ['Agência', 'agencia'],
                ['Conta', 'conta'],
              ] as const
            )
              .filter(([, chave]) => p.instrucoesPagamento?.[chave])
              .map(([rotulo, chave]) => (
                <div key={chave} className="flex justify-between gap-4">
                  <dt className="text-slate-500">{rotulo}</dt>
                  <dd className="font-medium text-slate-800 tabular">
                    {p.instrucoesPagamento![chave]}
                  </dd>
                </div>
              ))}
          </dl>
          <div className="mt-4">
            <Anexo pedidoId={p.id} papel="comprovante_pagamento" rotulo="Anexar comprovante" />
          </div>
        </Cartao>
      )}

      {/* ---------------------------------------------------------- intake */}
      {/* Qual formulário aparece vem do DADO da categoria, nunca do slug: uma
          terceira categoria precisa poder chegar sem tocar nesta tela. */}
      {p.categoria?.formaIntake === 'demonstracoes_contabeis' && editavel && (
        <div className="flex justify-end">
          <Link to={`/pedidos/novo?pedido=${p.id}`}>
            <Botao variante="secundario" tamanho="sm">
              ⛶ Abrir no editor completo (XML, IA, capa e prévia lado a lado)
            </Botao>
          </Link>
        </div>
      )}
      {p.categoria?.formaIntake === 'demonstracoes_contabeis' && (
        <DemonstracoesDoPedido
          pedidoId={p.id}
          somenteLeitura={!editavel}
          cabecalho={{
            razaoSocial: p.organizacao?.razaoSocial ?? '',
            cnpj: p.organizacao?.cnpj ?? null,
            logoUrl: p.organizacao?.logoUrl ?? null,
          }}
        />
      )}

      {p.categoria?.formaIntake !== 'demonstracoes_contabeis' && editavel && (
        <Cartao
          titulo="Relação de concluintes"
          descricao="Aceita .csv e .xlsx com as colunas nome, curso, ano e (opcional) turma."
        >
          <Aviso tom="info">
            O arquivo é conferido no seu computador antes de ser enviado. Se ele trouxer CPF,
            data de nascimento, telefone ou e-mail, o envio é recusado aqui mesmo — esses dados
            não podem ser publicados (Res. CEE nº 44/2020, Art. 5º) e não precisam sair da sua
            máquina.
          </Aviso>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={entradaArquivo}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void aoEscolherArquivo(f)
                e.target.value = ''
              }}
            />
            <Botao carregando={enviando} onClick={() => entradaArquivo.current?.click()}>
              {p.arquivoOriginalId ? 'Trocar arquivo' : 'Escolher arquivo'}
            </Botao>
            {p.totalConcluintes > 0 && (
              <span className="text-sm text-slate-500">
                {p.totalConcluintes} registros na relação atual
              </span>
            )}
          </div>

          {previa && (
            <div className="mt-4">
              <Aviso tom={previa.bloqueado ? 'perigo' : previa.problemas.length ? 'atencao' : 'sucesso'}>
                <p className="font-medium">{previa.resumo}</p>
                {previa.problemas.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[13px]">
                    {previa.problemas.map((x, i) => (
                      <li key={i}>
                        Linha {x.linha}
                        {x.coluna ? ` · ${x.coluna}` : ''}: {x.mensagem}
                      </li>
                    ))}
                  </ul>
                )}
              </Aviso>
            </div>
          )}
        </Cartao>
      )}

      {p.categoria?.formaIntake !== 'demonstracoes_contabeis' && p.totalConcluintes > 0 && (
        <RelacaoNominal pedidoId={p.id} total={p.totalConcluintes} />
      )}

      {/* -------------------------------------------------------- diagramação */}
      {admin && ['em_validacao', 'aguardando_sec', 'aguardando_aprovacao'].includes(p.status) && (
        <Cartao
          titulo="Diagramação"
          descricao="Anexe a prova diagramada e informe a medida — é ela que apura o valor."
        >
          <Anexo pedidoId={p.id} papel="materia" rotulo="Anexar prova diagramada" />
        </Cartao>
      )}

      {/* ------------------------------------------------------------- ação */}
      <Modal
        aberto={acaoAberta !== null}
        aoFechar={() => setAcaoAberta(null)}
        titulo={acaoAberta?.rotulo ?? ''}
      >
        <p className="text-sm text-slate-600">{acaoAberta?.descricao}</p>

        <div className="mt-4 space-y-4">
          {acaoAberta?.exigeMotivo && (
            <AreaTexto
              rotulo="Motivo"
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              ajuda="Pelo menos 10 caracteres. É o que a outra parte vai ler."
            />
          )}

          {acaoAberta?.para === 'aguardando_aprovacao' && (
            <div className="grid grid-cols-2 gap-4">
              <Campo
                rotulo="Altura (cm)"
                type="number"
                min={1}
                max={300}
                value={alturaCm}
                onChange={(e) => setAlturaCm(e.target.value)}
              />
              <Campo
                rotulo="Colunas"
                type="number"
                min={1}
                max={6}
                value={colunas}
                onChange={(e) => setColunas(e.target.value)}
              />
            </div>
          )}

          {acaoAberta?.para === 'publicado' && (
            <>
              <Campo
                rotulo="Edição do DOE"
                value={edicaoDoe}
                onChange={(e) => setEdicaoDoe(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Campo
                  rotulo="Data da publicação"
                  type="date"
                  value={dataPublicacao}
                  onChange={(e) => setDataPublicacao(e.target.value)}
                />
                <Campo rotulo="Página" value={pagina} onChange={(e) => setPagina(e.target.value)} />
              </div>
            </>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setAcaoAberta(null)}>
            Cancelar
          </Botao>
          <Botao
            carregando={transicionar.isPending}
            disabled={!acaoPreenchida()}
            onClick={confirmarAcao}
          >
            Confirmar
          </Botao>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------

/** Loads the statements, then hands them to the editor. */
function DemonstracoesDoPedido({
  pedidoId,
  somenteLeitura,
  cabecalho,
}: {
  pedidoId: string
  somenteLeitura: boolean
  cabecalho: { razaoSocial: string; cnpj: string | null; logoUrl?: string | null }
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['demonstracoes', pedidoId],
    queryFn: () =>
      api.get<{
        dados: Demonstracao[]
        capa?: { imagemUrl?: string | null; titulo?: string; subtitulo?: string; grafico?: boolean } | null
      }>(`/api/v1/pedidos/${pedidoId}/demonstracoes`),
  })
  // Estado vivo do editor — a prévia acompanha cada tecla, como no criador.
  const [aoVivo, setAoVivo] = useState<{ lista: Demonstracao[]; colunas: number } | null>(null)

  if (isLoading) return <Carregando />

  const lista = aoVivo?.lista ?? data?.dados ?? []

  return (
    <div className="space-y-5">
      <EditorDemonstracoes
        pedidoId={pedidoId}
        inicial={data?.dados ?? []}
        somenteLeitura={somenteLeitura}
        aoMudar={(l, c) => setAoVivo({ lista: l, colunas: c })}
        aoSalvar={() => {
          void qc.invalidateQueries({ queryKey: ['pedido', pedidoId] })
          void qc.invalidateQueries({ queryKey: ['demonstracoes', pedidoId] })
        }}
      />
      {lista.length > 0 && (
        <Cartao
          titulo="Prévia diagramada"
          descricao="Como a matéria compõe no padrão do Diário — a régua marca centímetros reais; a medição oficial é da EGBA."
        >
          <PreviaDiario
            cabecalho={cabecalho}
            demonstracoes={lista}
            colunas={aoVivo?.colunas ?? 2}
            capa={data?.capa ?? null}
          />
        </Cartao>
      )}
    </div>
  )
}

/**
 * The nominal list, behind a click.
 *
 * Not shown by default: opening the order does not require seeing 800 minors'
 * names, and every fetch of this writes a row in
 * `auditoria.acesso_dados_sensiveis`. Loading it automatically would fill that
 * table with accesses nobody meant to make, drowning the ones that matter.
 */
function RelacaoNominal({ pedidoId, total }: { pedidoId: string; total: number }) {
  const [aberto, setAberto] = useState(false)
  const [pagina, setPagina] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['concluintes', pedidoId, pagina],
    queryFn: () =>
      api.get<{ dados: Array<{ id: string; nomeCompleto: string; curso: string; anoConclusao: number; turma: string | null }>; total: number }>(
        `/api/v1/pedidos/${pedidoId}/concluintes?pagina=${pagina}&porPagina=100`,
      ),
    enabled: aberto,
  })

  const paginas = Math.max(1, Math.ceil(total / 100))

  return (
    <Cartao
      titulo="Relação nominal"
      descricao={`${total} registros. Todo acesso a esta lista é registrado em auditoria.`}
      acoes={
        <Botao variante="secundario" tamanho="sm" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Ocultar' : 'Ver relação'}
        </Botao>
      }
    >
      {!aberto ? (
        <p className="text-sm text-slate-500">
          A lista contém nomes de pessoas, em boa parte menores de idade. Ela fica oculta até que
          você peça para vê-la.
        </p>
      ) : isLoading ? (
        <Carregando />
      ) : (
        <>
          <Tabela>
            <Cabecalho colunas={['#', 'Nome', 'Curso', 'Ano', 'Turma']} />
            <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
              {data?.dados.map((c, i) => (
                <tr key={c.id}>
                  <td className="px-4 py-2 text-xs text-slate-400 tabular">
                    {(pagina - 1) * 100 + i + 1}
                  </td>
                  <td className="px-4 py-2 text-slate-800">{c.nomeCompleto}</td>
                  <td className="px-4 py-2 text-slate-600">{c.curso}</td>
                  <td className="px-4 py-2 text-slate-600 tabular">{c.anoConclusao}</td>
                  <td className="px-4 py-2 text-slate-600">{c.turma ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </Tabela>
          {paginas > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                Página {pagina} de {paginas}
              </span>
              <div className="flex gap-2">
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((x) => x - 1)}
                >
                  Anterior
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={pagina >= paginas}
                  onClick={() => setPagina((x) => x + 1)}
                >
                  Próxima
                </Botao>
              </div>
            </div>
          )}
        </>
      )}
    </Cartao>
  )
}

function Anexo({
  pedidoId,
  papel,
  rotulo,
}: {
  pedidoId: string
  papel: 'materia' | 'comprovante_pagamento'
  rotulo: string
}) {
  const qc = useQueryClient()
  const entrada = useRef<HTMLInputElement>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  async function enviar(arquivo: File): Promise<void> {
    setEnviando(true)
    setErro(null)
    try {
      const json = await api.enviarArquivo<Item<{ nome: string }>>(
        `/api/v1/pedidos/${pedidoId}/anexo?papel=${papel}`,
        arquivo,
      )
      setOk(`${json.dados.nome} anexado.`)
      void qc.invalidateQueries({ queryKey: ['pedido', pedidoId] })
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha no envio.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={entrada}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void enviar(f)
          e.target.value = ''
        }}
      />
      <Botao variante="secundario" carregando={enviando} onClick={() => entrada.current?.click()}>
        {rotulo}
      </Botao>
      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {ok && !erro && <Aviso tom="sucesso">{ok}</Aviso>}
    </div>
  )
}
